import { useSublinesVisibility } from "@/hooks/useSublinesOverlinesVisibility";
import { Options_Modul_Tabellen } from "@aleksundshantu/schirdewan-standard-types/acf-module-options";
import { Modul_Tabellen } from "@aleksundshantu/schirdewan-standard-types/acf-module";
import React from "react";
import { Section } from "../../components/Section/Section";
import { Content } from "../../components/Content/Content";
import { passACFLink } from "@/utils/passACFLink";
import { columnSize } from "@/utils/layout/columnSize";
import styles from "./Table.module.scss";
import { checkModuleIsHidden } from "@/utils/checkModuleIsHidden";
import { Button } from "@/components/Button/Button";
import { sanitizeAndStyleParagraph } from "@/utils/customSanitize";

export type TableModuleType = Modul_Tabellen;

interface TableProps {
  data: TableModuleType;
  options: Options_Modul_Tabellen;
}

const Table = ({ data, options }: TableProps) => {
  const {
    background_color_class,
    id,
    section_margin_class,
    section_padding_class,
    headline_text_align_class,
    text_align_class,
    module_width,
    table_head_mobile_linebreak,
  } = data.layout;

  {
    /* jscpd:ignore-start */
  }

  const { table_head, table_row, show_text_editor } = options;
  const {
    table_head_background_color,
    table_head_color_mobile,
    table_head_line_color,
    table_head_line_height,
    show_table_head_line,
  } = table_head;
  const tableHeadColorStyle = {
    "--table-head-color-mobile": table_head_color_mobile
      ? "var(--" + table_head_color_mobile + ")"
      : "var(--text-color)",
    "--table-head-background-color": table_head_background_color
      ? "var(--" + table_head_background_color + ")"
      : "var(--white)",
  };
  const tableHeadLineStyle = show_table_head_line
    ? {
        "--table-head-line":
          table_head_line_height +
          "px solid var(--" +
          table_head_line_color +
          ")",
      }
    : {};
  const {
    table_row_background_color,
    show_table_row_background_color,
    table_row_background_color_alternate,
    table_row_line_color,
    table_row_line_height,
    show_table_row_line,
  } = table_row;
  const tableRowLineStyle = show_table_row_line
    ? {
        "--table-row-line":
          table_row_line_height +
          "px solid var(--" +
          table_row_line_color +
          ")",
      }
    : {};

  const { showOverline, showSubline } = useSublinesVisibility(
    options?.show_overline_subline ?? "none",
  );

  //! check if module is hidden
  const isHidden = checkModuleIsHidden(data.settings);
  if (isHidden) {
    return null;
  }
  {
    /* jscpd:ignore-end */
  }

  const { table } = data.tables;

  const getColumns = <T,>(obj: { [k: string]: T }, name: string): T[] => {
    const number = table.number_columns;

    return Array(number)
      .fill(0)
      .map((_, i) => {
        return obj[name + "_" + (i + 1)];
      });
  };

  const headVariables = getColumns(table.table_heads, "table_head")
    .map((e, key) => {
      return {
        ["--column-" + (key + 1) + "-head"]:
          `"${e}${table_head_mobile_linebreak ? "" : ":"} "`,
      };
    })
    .reduce((prev, curr) => {
      return {
        ...prev,
        ...curr,
      };
    }, {});

  return (
    <Section
      className={`is-clipped ${styles["table"]} ${background_color_class} ${section_padding_class} ${section_margin_class} ${table_head_mobile_linebreak ? styles["table-head-mobile-linebreak"] : ""}`}
      columnsClassName="is-centered"
      id={id}
      style={
        {
          ...headVariables,
          ...tableRowLineStyle,
          ...tableHeadColorStyle,
          ...tableHeadLineStyle,
        } as React.CSSProperties
      }
    >
      <div
        className={`column ${module_width === "normal-frame" ? "is-12" : columnSize(options.columns)}`}
      >
        <Content
          subline={showSubline ? data.tables.subline : ""}
          overline={showOverline ? data.tables.overline : ""}
          headline={data.tables.headline}
          headline_html_tag={data.tables.headline_html_tag}
          text={""}
          headline_text_align_class={headline_text_align_class}
          text_align_class={text_align_class}
          link={false}
        />
        <table>
          <thead>
            <tr>
              {getColumns(table.table_heads, "table_head").map(
                (column, key) => {
                  return (
                    <th key={key} className="table-head">
                      {column}
                    </th>
                  );
                },
              )}
            </tr>
          </thead>
          <tbody>
            {table.table_row &&
              table.table_row.map((row, rowIndex) => {
                let styleProps = { style: { backgroundColor: "none" } };
                if (show_table_row_background_color) {
                  if (
                    table_row_background_color &&
                    table_row_background_color_alternate
                  ) {
                    styleProps =
                      rowIndex % 2 === 1
                        ? {
                            style: {
                              backgroundColor: `var(--${table_row_background_color_alternate})`,
                            },
                          }
                        : {
                            style: {
                              backgroundColor: `var(--${table_row_background_color})`,
                            },
                          };
                  } else if (table_row_background_color) {
                    styleProps = {
                      style: {
                        backgroundColor: `var(--${table_row_background_color})`,
                      },
                    };
                  }
                }

                return (
                  <tr
                    key={rowIndex}
                    className={styles["table-row"]}
                    {...styleProps}
                  >
                    {getColumns(row, show_text_editor ? "content" : "text").map(
                      (column, rowIndex) => {
                        return (
                          <td key={rowIndex} className="table-head">
                            {show_text_editor ? (
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: sanitizeAndStyleParagraph(
                                    column,
                                    "table-text",
                                  ),
                                }}
                              />
                            ) : (
                              <span className="table-text">{column} </span>
                            )}
                          </td>
                        );
                      },
                    )}
                  </tr>
                );
              })}
          </tbody>
          <tfoot>
            <tr>
              <td
                className={`has-text-right ${styles.note} px-0`}
                colSpan={Number(table.number_columns)}
              >
                {table.footer}
              </td>
            </tr>
          </tfoot>
        </table>

        <div
          className={`${styles["table-content"]}`}
          dangerouslySetInnerHTML={{ __html: data.tables.content }}
        />

        {data.tables.link && (
          <Button type={"primary"} {...passACFLink(data.tables, "link")}>
            {data.tables.link.title}
          </Button>
        )}
      </div>
    </Section>
  );
};

export default Table;
